// File: /pages/ShuffleMaps/EventsCalendar.jsx
"use client";

import { useEffect, useMemo, useState } from "react";
import styles from "./EventsCalendar.module.css";
import ListingCard from "/components/Shufflemaps/Listings/ListingCard";

const tryExtractDate = (listing) => {
  // Best-effort parse: look for an ISO-like date inside schedule (string or object)
  const s = listing?.schedule;
  if (!s) return null;
  try {
    if (typeof s === "string") {
      // find YYYY-MM-DD
      const m = s.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
      if (m) return new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00Z`);
    } else if (typeof s === "object") {
      if (s.date) return new Date(s.date);
      if (Array.isArray(s.dates) && s.dates.length) return new Date(s.dates[0]);
    }
  } catch {}
  return null;
};

export default function EventsCalendarPage() {
  const [categories, setCategories] = useState([]);
  const [listings, setListings] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("all"); // "YYYY-MM"
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [cr, lr] = await Promise.all([
          fetch("/api/shufflemaps/getAvailableCategories"),
          fetch("/api/shufflemaps/listings/get-listings"),
        ]);
        const cats = await cr.json();
        const list = await lr.json();
        if (!alive) return;
        setCategories(Array.isArray(cats) ? cats : []);
        setListings(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("EventsCalendar load error", e);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  const eventsCatId = useMemo(
    () => categories.find((c) => c.name === "Events")?.id ?? null,
    [categories]
  );

  const eventRows = useMemo(() => {
    const raw = (listings || []).filter((l) => {
      const ids = Array.isArray(l.categories)
        ? l.categories
            .map((n) => categories.find((c) => c.name === n)?.id)
            .filter((v) => v != null)
        : Array.isArray(l.categoryIds) ? l.categoryIds : [];
      return eventsCatId != null ? ids.includes(eventsCatId) : false;
    });

    // attach parsed date if available
    return raw
      .map((l) => ({ ...l, __date: tryExtractDate(l) }))
      .sort((a, b) => {
        const da = a.__date ? +a.__date : Infinity;
        const db = b.__date ? +b.__date : Infinity;
        return da - db;
      });
  }, [listings, categories, eventsCatId]);

  const monthOptions = useMemo(() => {
    const set = new Set(
      eventRows
        .filter((r) => r.__date)
        .map((r) => `${r.__date.getUTCFullYear()}-${String(r.__date.getUTCMonth() + 1).padStart(2, "0")}`)
    );
    return ["all", ...Array.from(set).sort()];
  }, [eventRows]);

  const filtered = useMemo(() => {
    if (selectedMonth === "all") return eventRows;
    return eventRows.filter((r) => {
      if (!r.__date) return false;
      const key = `${r.__date.getUTCFullYear()}-${String(r.__date.getUTCMonth() + 1).padStart(2, "0")}`;
      return key === selectedMonth;
    });
  }, [eventRows, selectedMonth]);

  return (
    <main className={styles.smMain}>
      <section className={styles.smSection}>
        <h1 className={styles.smTitle}>Events Calendar</h1>
        <p className={styles.smSub}>
          Upcoming shuffle events. Select a month or view all. Dates are shown when available from listing schedules.
        </p>

        <div className={styles.toolbar}>
          <label className={styles.label}>
            Month:
            <select
              className={styles.select}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {m === "all" ? "All" : m}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.count}>
            {loading ? "Loading…" : `${filtered.length} event${filtered.length === 1 ? "" : "s"}`}
          </div>
        </div>

        {loading ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>Loading…</div>
            <div className={styles.emptySub}>Fetching events.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>No events found</div>
            <div className={styles.emptySub}>Try “All” months or check back soon.</div>
          </div>
        ) : (
          <div className={styles.cardsGrid}>
            {filtered.map((l) => (
              <div key={l.id} className={styles.cardWrap}>
                {l.__date && (
                  <div className={styles.dateBadge}>
                    {l.__date.getUTCFullYear()}-{String(l.__date.getUTCMonth() + 1).padStart(2, "0")}-
                    {String(l.__date.getUTCDate()).padStart(2, "0")}
                  </div>
                )}
                <a
                  href={`/ShuffleMaps?listingId=${encodeURIComponent(String(l.id))}`}
                  className={styles.cardLink}
                  title="Open in ShuffleMaps"
                >
                  <ListingCard
                    listingId={l.id}
                    name={l.name}
                    status={l.status}
                    shortDescription={l.short_description ?? l.shortDescription}
                    activeThumbnailUrl={l.active_thumbnail_url ?? l.activeThumbnailUrl ?? null}
                    suppressEmptyRatingDisplay
                  />
                </a>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
